import { useTheme } from '@emotion/react'
import {
    BreachEvent,
    click_origin_breach_screen,
    DataLayerEventName,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import React, { useEffect } from 'react'
import { getAttribution } from '../../../../../errors/ErrorPage'
import { useAuthActions } from '../../../../../user-registration/authentication/AuthenticationProvider'
import { createLinkClickEvent } from '../../../createLinkClickHandler'
import {
    StyledBlackFridayBreachContent,
    StyledButton,
    StyledHelpMessage,
    StyledMessage,
    StyledPackage,
    StyledRegistrationInfo,
    StyledTextLink,
    StyledTitle,
} from './BlackFridayBreachContent.styled'
import BlackFridaySubscriptionPackage from './BlackFridayComponents/BlackFridaySubscriptionPackage'
import { SubscriptionPackages } from '../../../common'
import { DEFAULT_CART_QUANTITY, toItemListId } from '../../../../..'

export interface BlackFridayBreachContentProps {
    title: string
    message: React.ReactNode
    cta: string
    subscriptionPackages?: SubscriptionPackages
    breachCreativeName: string
    breachType: string
    breachDeal: string
    onEvent: (event: BreachEvent) => void
}

export const BlackFridayBreachContent: React.FC<BlackFridayBreachContentProps> =
    ({
        title,
        message,
        cta,
        subscriptionPackages,
        breachCreativeName,
        breachDeal,
        breachType,
        onEvent,
    }) => {
        const { onSubscribeClick } = useAuthActions()

        const theme = useTheme()
        const masthead =
            getAttribution(theme.kind, theme.section) || 'The West Australian'

        useEffect(() => {
            const viewSubscriptionItems: ViewSubscriptionItem[] =
                subscriptionPackages?.map((pkg, index) => ({
                    index,
                    deal_name: breachType,
                    item_name: pkg.heading,
                    item_id: pkg.rateCode,
                    item_brand: masthead,
                    component_path: pkg.packagePath,
                    item_list_category: 'freemium',
                    item_list_category2: 'Premium - Inline',
                    item_list_name: breachCreativeName,
                    item_list_id: toItemListId(breachCreativeName),
                    price: pkg.weeklyPrice,
                    promo_price: pkg.promoPrice || 0,
                    discount: pkg.discount || 0,
                    coupon: pkg.offerCode || "",
                    quantity: DEFAULT_CART_QUANTITY,
                    currency: 'AUD',
                })) || []

            onEvent({
                type: DataLayerEventName.viewSubscriptionItemListEvent,
                parentType: DataLayerEventName.viewItemListEvent,
                originator: 'breachscreen',
                payload: { items: viewSubscriptionItems },
            })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])

        return (
            <StyledBlackFridayBreachContent>
                <StyledRegistrationInfo>
                    <StyledTitle>{title}</StyledTitle>
                    <StyledMessage>{message}</StyledMessage>

                    <StyledButton
                        onClick={() => {
                            createLinkClickEvent(
                                breachCreativeName,
                                breachDeal,
                                onEvent,
                            )()
                            onSubscribeClick({
                                callToAction: click_origin_breach_screen,
                            })
                        }}
                    >
                        {cta}
                    </StyledButton>
                    {/* <StyledButtonCaption>
                        Offer only available online.
                    </StyledButtonCaption> */}
                    <StyledHelpMessage>
                        Need Help?{' '}
                        <StyledTextLink
                            setBlackFridayColorScheme={true}
                            href="tel:1800 811 855"
                        >
                            1800 811 855
                        </StyledTextLink>
                        .
                    </StyledHelpMessage>
                </StyledRegistrationInfo>
                <StyledPackage>
                    {subscriptionPackages &&
                        subscriptionPackages.map(
                            (subscriptionPackageItem, index) => {
                                return (
                                    <BlackFridaySubscriptionPackage
                                        key={index}
                                        breachCreativeName={breachCreativeName}
                                        breachDeal={breachDeal}
                                        onEvent={onEvent}
                                        subscriptionPackage={subscriptionPackageItem}
                                        theme={theme}
                                    />
                                )
                            },
                        )}
                </StyledPackage>
            </StyledBlackFridayBreachContent>
        )
    }

BlackFridayBreachContent.displayName = 'BlackFridayBreachContent'

export default BlackFridayBreachContent
